import React from 'react';
import { motion } from 'framer-motion';
import { Code, Database, Cloud, Smartphone, Globe, BarChart3 } from 'lucide-react';

interface SkillCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  skills: string[];
  color: string;
}

const skillCategories: SkillCategory[] = [
  {
    id: 'product',
    title: 'Product Management',
    icon: <BarChart3 className="w-6 h-6" />,
    skills: ['Product Roadmap Development', 'Backlog Management', 'MVP Definition', 'SAFe', 'SaaS', 'Observability', 'OKRs', 'Stakeholder Management'],
    color: 'from-indigo-500 to-blue-500'
  },
  {
    id: 'data',
    title: 'Data Driven Decision Making',
    icon: <Database className="w-6 h-6" />,
    skills: ['SQL', 'Google Analytics', 'SmartLook', 'Tableau', 'A/B Testing', 'Data Analysis', 'Performance Metrics'],
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'technical',
    title: 'Technical Skills',
    icon: <Code className="w-6 h-6" />,
    skills: ['Java', 'SQL', 'APIs', 'AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'OpenTelemetry'],
    color: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'cloud',
    title: 'Cloud & DevOps',
    icon: <Cloud className="w-6 h-6" />,
    skills: ['AWS', 'GCP', 'Azure', 'Docker', 'Kubernetes', 'CI/CD', 'Git', 'Cloud Migration', 'FedRAMP'],
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'search',
    title: 'Search & Analytics',
    icon: <Globe className="w-6 h-6" />,
    skills: ['Solr', 'Lucene', 'Search Algorithms', 'Autocomplete', 'Product Discovery', 'Customer Analytics'],
    color: 'from-teal-500 to-cyan-500'
  },
  {
    id: 'leadership',
    title: 'Leadership & Communication',
    icon: <Smartphone className="w-6 h-6" />,
    skills: ['Team Leadership', 'Mentoring', 'Public Speaking', 'Conference Speaking', 'Cross-functional Collaboration', 'Agile/Scrum'],
    color: 'from-orange-500 to-red-500'
  }
];

const Skills: React.FC = () => {
  return (
    <section id="skills" className="section-padding bg-white">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Skills & Expertise
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A comprehensive set of technical and product management skills that enable me 
            to build and lead successful digital products from concept to launch.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {skillCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${category.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {category.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {category.title}
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  {category.skills.map((skill, skillIndex) => (
                    <motion.span
                      key={skillIndex}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: skillIndex * 0.05 }}
                      viewport={{ once: true }}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-primary-100 hover:text-primary-800 transition-colors duration-200"
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
